import React, { useCallback, useEffect, useMemo, useState } from "react";
import { safeStringValue } from "util/ApiHelper";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { useDispatch, useSelector } from "react-redux";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { MY_EXIM_API_RFQ } from "../../appRedux/services/requestQuotationsNetwork";
import { isArrayNonEmpty, isValidString } from "../../util/ApiHelper";
import { formatDate, showAlert } from "../../util/helper";
import { BUSINESS_TYPE } from "../../util/Constant";
import RFQStatusTags from "../../routes/requestQuotation/RFQStatus";
import { Card, Dropdown, Table } from "antd";
import { EditOutlined, LeftOutlined, MoreOutlined } from "@ant-design/icons";
import { ItemDetailMobile } from "../../components/ItemDetailMobile";

const getPricePerUnit = (record) => {
  return `${safeStringValue(record?.currencyCode)} ${safeStringValue(
    record?.price
  )} / ${safeStringValue(
    record?.enquiries?.units,
    record?.enquiries?.shipmentMode === "sea"
      ? record?.enquiries?.shipmentType === "fcl"
        ? `Unit (${record?.enquiries?.containerType})`
        : "Cubic Meter"
      : "Cubic Meter"
  )}`;
};

const ViewQuotationAdmin = () => {
  const [quotationList, SetQuotationList] = useState(null);
  const history = useHistory();
  const { isMobile } = useGetBreakpoints();
  const dispatch = useDispatch();
  const { loading } = useSelector(({ common }) => common);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const party = location?.state?.party;
  const navigateToDetails = useCallback(
    (id) => {
      history.push(`/enquiries/view-quotations-details?id=${id}`);
    },
    [history]
  );
  const countries = useSelector(({ common }) => common.countries);

  const getSymbolForCurrency = (currencyCode) => {
    const country = countries.find((c) => c.currencyCode === currencyCode);
    return country ? country.curencySymbol : "";
  };

  const callQuotationList = useCallback(
    (page = 1, limit = 10) => {
      dispatch(fetchStart());
      MY_EXIM_API_RFQ.getQuotationList({ page, limit, enquiryId: id })
        .then((response) => {
          let dataItem = response?.data?.data;
          if (dataItem) {
            SetQuotationList(dataItem);
          }
          dispatch(fetchSuccess());
        })
        .catch((e) => {
          if (isValidString(e?.message)) {
            showAlert(e?.message);
          }
          dispatch(fetchSuccess());
        });
    },
    [dispatch, id]
  );

  useEffect(() => {
    callQuotationList();
  }, [callQuotationList]);

  const columns = useMemo(() => {
    return [
      {
        title: "Received Date",
        key: "createdAt",
        render: (record) => <div>{formatDate(record?.createdAt)}</div>,
      },
      {
        title: "Product Name",
        key: "productName",
        sorter: true,
        render: (record) => <div>{record?.enquiries?.productName}</div>,
      },
      {
        title: "Company Name",
        key: "user",
        render: (record) => (
          <div className="highlighted-column">
            {record?.sender?.companyName}
          </div>
        ),
      },
      ...(party === BUSINESS_TYPE.EXPORTER
        ? [
            {
              title: "Delivery Terms",
              key: "deliveryTerms",
              render: (record) =>
                record?.paymentTerms !== null ? (
                  <div>{record?.paymentTerms}</div>
                ) : null,
            },
          ]
        : []),

      ...(party !== BUSINESS_TYPE.CUSTOM_HOUSE_AGENT
        ? [
            {
              title: "Price",
              key: "price",
              sorter: true,
              render: (record) => <div>{getPricePerUnit(record)}</div>,
            },
          ]
        : []),
      {
        title: "Status",
        key: "country",
        sorter: true,
        render: (record) => (
          <div>
            <RFQStatusTags status={record?.status} />
          </div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        render: (record) => (
          <Dropdown
            menu={{
              items: [
                {
                  label: "View Details",
                  key: "1",
                  icon: <EditOutlined />,
                  onClick: () => {
                    navigateToDetails(record?.id);
                  },
                },
              ],
            }}
          >
            <div
              className="myexim-action-icon"
              onClick={(e) => e.preventDefault()}
            >
              <MoreOutlined />
            </div>
          </Dropdown>
        ),
      },
    ];
  }, [navigateToDetails]);

  const renderContent = useCallback(
    (data) => {
      return isMobile ? (
        data.map((record) => {
          return (
            <ItemDetailMobile
              postedDate={formatDate(record.createdAt)}
              primaryRow={{
                text: record.enquiries?.productName,
                subText: getPricePerUnit(record),
              }}
              secondaryRow={{
                text: record.sender?.companyName,
              }}
              status={record?.status}
              StatusComponent={RFQStatusTags}
              handleNavigation={() => {
                navigateToDetails(record.id);
              }}
            />
          );
        })
      ) : (
        <Table
          rowClassName={(data, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          className="gx-table-responsive"
          columns={columns}
          dataSource={quotationList?.rows}
          pagination={{
            position: ["bottomRight"],
            pageSize: 10,
            showSizeChanger: true,
            onChange: callQuotationList,
          }}
          onRow={(record) => {
            return {
              onClick: (e) => {
                // Add specific conditions here for menu items
                if (
                  !e.target.classList.contains(
                    "ant-dropdown-menu-title-content"
                  )
                ) {
                  navigateToDetails(record.id);
                }
              },
            };
          }}
        />
      );
    },
    [
      callQuotationList,
      columns,
      isMobile,
      navigateToDetails,
      quotationList?.rows,
    ]
  );

  return (
    <Card
      title={
        <div className="myexim-table-title">
          <LeftOutlined
            className={"back-icon"}
            size="large"
            onClick={() => {
              history.goBack();
            }}
          />
          Quotations
        </div>
      }
    >
      {loading ? null : isArrayNonEmpty(quotationList?.rows) ? (
        renderContent(quotationList.rows)
      ) : (
        <div style={{ textAlign: "center", padding: "20px" }}>
          No Quotation has been raised against this enquiry id {id}
        </div>
      )}
    </Card>
  );
};

export default ViewQuotationAdmin;
